import React, { useState, useMemo, useEffect } from 'react';
import PageSeo from '../components/Common/PageSeo';
import { useTranslations } from '../locales/useTranslations';
import { useRouter } from 'next/router';
import Navbar from '../components/Layouts/Navbar';
import Footer from '../components/Layouts/Footer';
import BlogHeader from '../components/Blog/BlogHeader';
import BlogFilter from '../components/Blog/BlogFilter';
import BlogList from '../components/Blog/BlogList';
import { fetchBlogPosts } from '../lib/blogApi';

const POSTS_PER_PAGE = 6;

function getUniqueCategories(posts) {
    const unique = [...new Set(posts.map((p) => p.category))];
    return unique.sort();
}

function getUniqueYears(posts) {
    const unique = [...new Set(posts.map((p) => {
        const match = p.date.match(/\d{4}/);
        return match ? match[0] : null;
    }).filter(Boolean))];
    return unique.sort((a, b) => b - a);
}

const Blog = ({ posts: allPosts = [] }) => {
    const { t } = useTranslations();
    const router = useRouter();
    const locale = router.locale || 'de';
    const baseUrl = locale === 'en' ? 'https://simplify-data.com' : 'https://simplify-data.de';

    // Filter States
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedYear, setSelectedYear] = useState('all');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);

    // Kategorien und Jahre aus geladenen Posts
    const categories = getUniqueCategories(allPosts);
    const years = getUniqueYears(allPosts);

    // Filter posts based on selected filters
    const filteredPosts = useMemo(() => {
        return allPosts.filter(post => {
            // Category filter
            const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;

            // Year filter
            const postYear = post.date.match(/\d{4}/)?.[0];
            const matchesYear = selectedYear === 'all' || postYear === selectedYear;

            return matchesCategory && matchesYear;
        });
    }, [allPosts, selectedCategory, selectedYear]);

    // Calculate total pages
    const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);

    // Get posts for current page
    const paginatedPosts = useMemo(() => {
        const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
        const endIndex = startIndex + POSTS_PER_PAGE;
        return filteredPosts.slice(startIndex, endIndex);
    }, [filteredPosts, currentPage]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, selectedYear]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        // Scroll to top of blog list
        window.scrollTo({ top: 400, behavior: 'smooth' });
    };

    // Reset all filters
    const handleReset = () => {
        setSelectedCategory('all');
        setSelectedYear('all');
        setCurrentPage(1);
    };

    return (
        <>
            <PageSeo
                title={t('blog_page_title')}
                description={t('blog_page_description')}
                canonical={`${baseUrl}/blog/`}
                openGraph={{
                    url: `${baseUrl}/blog/`,
                    title: t('blog_page_title'),
                    description: t('blog_page_description'),
                    images: [
                        {
                            url: `${baseUrl}/images/simplify-data.png`,
                            width: 1200,
                            height: 1200,
                            alt: t('blog_page_title'),
                            type: 'image/jpeg',
                        },
                    ],
                    siteName: t('blog_page_title'),
                    locale: 'de_DE',
                    type: 'website'
                }}
                twitter={{
                    handle: '@simplify_data',
                    site: '@simplify_data',
                    cardType: 'summary_large_image',
                }}
                additionalMetaTags={[
                    {
                        name: 'robots',
                        content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
                    },
                    {
                        name: 'keywords',
                        content: t('blog_page_keywords'),
                    },
                    {
                        name: 'author',
                        content: 'Simplify Data'
                    },
                    {
                        name: 'geo.region',
                        content: 'DE-SH'
                    },
                    {
                        name: 'geo.placename',
                        content: 'Kiel'
                    },
                    {
                        property: 'article:modified_time',
                        content: new Date().toISOString()
                    },
                    {
                        name: 'format-detection',
                        content: 'telephone=no'
                    }
                ]}
                additionalLinkTags={[
                    {
                        rel: 'icon',
                        href: `${baseUrl}/images/favicon.png`,
                        type: 'image/png'
                    },
                    {
                        rel: 'alternate',
                        hrefLang: 'de',
                        href: 'https://simplify-data.de/blog/'
                    },
                    {
                        rel: 'alternate',
                        hrefLang: 'en',
                        href: 'https://simplify-data.com/blog/'
                    }
                ]}
            />
            <Navbar />
            <BlogHeader />
            {allPosts.length > 0 && (
                <BlogFilter
                    categories={categories}
                    years={years}
                    selectedCategory={selectedCategory}
                    selectedYear={selectedYear}
                    onCategoryChange={setSelectedCategory}
                    onYearChange={setSelectedYear}
                    onReset={handleReset}
                />
            )}
            <BlogList
                posts={paginatedPosts}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={false}
                hasError={false}
            />
            <Footer />
        </>
    );
};

export async function getStaticProps({ locale }) {
    try {
        const posts = await fetchBlogPosts(locale || 'de');
        return {
            props: { posts },
            revalidate: 300, // ISR: alle 5 Min. neu validieren
        };
    } catch {
        return { props: { posts: [] }, revalidate: 60 };
    }
}

export default Blog;
